import {
  Accordion,
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  DropdownMenu,
  Flex,
  FormControlLabel,
  FormField,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSection,
  ConfirmDialog,
  Modal,
  MultiSelect,
  Radio,
  RadioGroup,
  Section,
  Select,
  Separator,
  Snackbar,
  Spinner,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  loadingMessage,
} from '@nrx/ui';
import {
  ArrowRight,
  Building2,
  Calendar,
  CircleHelp,
  ClipboardList,
  Home,
  Sun,
  Truck,
  UserRound,
  X,
} from '@nrx/ui/icons';
import { useState, type JSX } from 'react';

/** Neutral preview surface for layout spacing demos (avoids `.card` fixed padding). */
const PREVIEW_ITEM =
  'rounded-md border border-dashed border-[var(--color-field-border)] bg-[var(--color-surface)]';

/* -------------------------------------------------------------------------- */
/* Component demos                                                            */
/* -------------------------------------------------------------------------- */

/** Button tone examples. */
export function ButtonDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3">
      <Flex gap="3" wrap>
        <Button tone="primary">Primary button</Button>
        <Button tone="secondary">Secondary button</Button>
        <Button tone="link">Learn more</Button>
      </Flex>
      <Text size="2" color="muted">
        Primary buttons use the coral action color without a drop shadow for a flat CTA style.
      </Text>
    </Flex>
  );
}

/** Disabled button example. */
export function ButtonDisabledDemos(): JSX.Element {
  return (
    <Flex gap="3" wrap>
      <Button tone="primary" disabled>
        Save
      </Button>
      <Button tone="secondary" disabled>
        Cancel
      </Button>
    </Flex>
  );
}

/** Text input states. */
export function TextInputDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="4" className="max-w-xs">
      <TextInput value="" onChange={() => {}} placeholder="Type here…" />
      <TextInput
        value="jane@example.com"
        onChange={() => {}}
        type="email"
        placeholder="you@example.com"
      />
      <TextInput value="" onChange={() => {}} placeholder="Disabled input" disabled />
    </Flex>
  );
}

/** Form field with help text. */
export function FormFieldHelpDemos(): JSX.Element {
  const [email, setEmail] = useState('');

  return (
    <div className="max-w-xs">
      <FormField
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        help="We will never share your email."
      />
    </div>
  );
}

/** Form field with validation error. */
export function FormFieldErrorDemos(): JSX.Element {
  const [email, setEmail] = useState('not-an-email');

  return (
    <div className="max-w-xs">
      <FormField
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        error="Please enter a valid email address."
      />
    </div>
  );
}

/** Badge variant swatches. */
export function BadgeDemos(): JSX.Element {
  return (
    <Flex gap="3" wrap>
      <Badge variant="neutral">Neutral</Badge>
      <Badge variant="success">Success</Badge>
      <Badge variant="warning">Warning</Badge>
      <Badge variant="danger">Danger</Badge>
      <Badge variant="info">Info</Badge>
      <Badge variant="accent">Accent</Badge>
    </Flex>
  );
}

/** Design-system status and membership badge rows. */
export function BadgeStatusDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="4">
      <Flex gap="3" wrap>
        <Badge variant="success" dot>
          Delivered
        </Badge>
        <Badge variant="accent" dot>
          On its way
        </Badge>
        <Badge variant="accent" emphasis="solid" dot>
          Delayed
        </Badge>
        <Badge variant="info" dot>
          Processing
        </Badge>
        <Badge variant="neutral" dot>
          Draft
        </Badge>
      </Flex>
      <Flex gap="3" wrap>
        <Badge variant="info" emphasis="solid">
          Active member
        </Badge>
        <Badge variant="accent" emphasis="solid">
          New
        </Badge>
        <Badge variant="info">Eligible</Badge>
        <Badge variant="accent" emphasis="outline">
          Featured
        </Badge>
        <Badge variant="neutral" emphasis="outline">
          Optional
        </Badge>
      </Flex>
    </Flex>
  );
}

/** Avatar sizes and fallback initials. */
export function AvatarDemos(): JSX.Element {
  return (
    <Flex gap="4" align="center" wrap>
      <Avatar alt="Jane Doe" size="sm" />
      <Avatar alt="Jane Doe" />
      <Avatar alt="Jane Doe" size="lg" />
      <Avatar alt="Jane Doe" src="https://i.pravatar.cc/80?u=jane" />
    </Flex>
  );
}

/** Card with title. */
export function CardWithTitleDemos(): JSX.Element {
  return (
    <Card title="Card title" className="max-w-md">
      <Text as="p" size="2" color="muted">
        Card body content goes here. Use cards to group related information.
      </Text>
    </Card>
  );
}

/** Card without title. */
export function CardWithoutTitleDemos(): JSX.Element {
  return (
    <Card className="max-w-md">
      <Text as="p" size="2" color="muted">
        Card body content without a title heading.
      </Text>
    </Card>
  );
}

/** Card elevation levels from the design-system deck. */
export function CardElevationDemos(): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {(
        [
          { elevation: 'flat', label: 'Flat', meta: 'border only' },
          { elevation: 'soft', label: 'Soft', meta: 'list cards' },
          { elevation: 'card', label: 'Card', meta: 'hero, modals' },
          { elevation: 'pop', label: 'Pop', meta: 'menus, toasts' },
        ] as const
      ).map(({ elevation, label, meta }) => (
        <Card key={elevation} elevation={elevation} className="min-h-24">
          <Text as="p" size="2" weight="bold">
            {label}
          </Text>
          <Text as="p" size="1" color="muted">
            {meta}
          </Text>
        </Card>
      ))}
    </div>
  );
}

/** Phase progress card pattern — teal vs brand-blue accent options. */
export function CardPhasePatternDemos(): JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="relative">
        <div className="absolute right-3 top-3 rounded-md bg-[var(--color-stone-light)] px-2 py-1 font-mono text-[10px] font-semibold tracking-wide text-[var(--color-steel)]">
          #52D9CB · Teal
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-teal-soft)] text-lg font-bold text-[#1F8A7D]">
            1
          </div>
          <div>
            <Text as="p" size="5" weight="bold">
              Phase 1
            </Text>
            <Text as="p" size="2" color="muted">
              Meals · Week 3 of 4
            </Text>
          </div>
        </div>
        <div className="mt-3.5 h-2 overflow-hidden rounded-full bg-[var(--color-stone-light)]">
          <div className="h-full w-[64%] rounded-full bg-[var(--color-teal)]" />
        </div>
        <Text as="p" size="1" color="muted" className="mt-2.5">
          64% complete · next delivery Thursday
        </Text>
      </Card>
      <Card className="relative">
        <div className="absolute right-3 top-3 rounded-md bg-[var(--color-stone-light)] px-2 py-1 font-mono text-[10px] font-semibold tracking-wide text-[var(--color-steel)]">
          #4095A5 · Blue
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-brand-blue-soft)] text-lg font-bold text-[var(--color-brand-blue)]">
            1
          </div>
          <div>
            <Text as="p" size="5" weight="bold">
              Phase 1
            </Text>
            <Text as="p" size="2" color="muted">
              Meals · Week 3 of 4
            </Text>
          </div>
        </div>
        <div className="mt-3.5 h-2 overflow-hidden rounded-full bg-[var(--color-stone-light)]">
          <div className="h-full w-[64%] rounded-full bg-[var(--color-brand-blue)]" />
        </div>
        <Text as="p" size="1" color="muted" className="mt-2.5">
          64% complete · next delivery Thursday
        </Text>
      </Card>
    </div>
  );
}

/** Delivery status card with coral header, progress, and Track action. */
export function CardDeliveryPatternDemos(): JSX.Element {
  return (
    <Card className="max-w-md overflow-hidden p-0">
      <div className="flex items-center justify-between bg-[var(--color-action-primary)] px-4 py-3.5">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-white">
          In transit
        </span>
        <span className="text-[11px] font-medium text-white">Order #4821</span>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-action-primary)] text-white">
            <Truck size={22} aria-hidden />
          </div>
          <div>
            <Text as="p" size="4" weight="bold">
              On its way
            </Text>
            <Text as="p" size="2" color="muted">
              Arrives Thursday · 9 AM – 1 PM
            </Text>
          </div>
        </div>
        <div className="mt-3.5 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-[var(--color-stone-light)]">
            <div
              className="h-full w-[70%] rounded-full"
              style={{
                background: 'linear-gradient(90deg, var(--color-coral), var(--color-peach-mid))',
              }}
            />
          </div>
          <span className="text-[11px] font-semibold text-[var(--color-ink-soft)]">
            2 stops away
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-[var(--color-border)] pt-3">
          <div>
            <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-ink-muted)]">
              Contents
            </div>
            <div className="mt-1 text-[13px] font-medium text-[var(--color-ink)]">
              14 meals · RDN-approved
            </div>
          </div>
          <Button tone="secondary" className="px-3.5 py-2 text-xs">
            Track <ArrowRight size={14} aria-hidden />
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** Up-next gradient card with three-segment phase progress. */
export function CardUpNextPatternDemos(): JSX.Element {
  return (
    <Card
      className="relative max-w-md overflow-hidden p-0"
      style={{
        background: 'linear-gradient(160deg, var(--color-brand-blue-soft) 0%, #C7E5E1 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute -right-8 -top-8 h-[140px] w-[140px] rounded-full bg-white/35"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-[-20px] right-10 h-[60px] w-[60px] rounded-full bg-white/25"
        aria-hidden
      />
      <div className="relative p-4">
        <div className="mb-3.5 flex items-center gap-2">
          <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--color-brand-blue)]">
            Up next
          </span>
          <span className="text-[11px] font-medium text-[var(--color-ink)] opacity-60">
            in 6 days
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[var(--color-brand-blue)] shadow-[0_2px_8px_rgba(1,38,31,0.08)]">
            <Truck size={22} aria-hidden />
          </div>
          <div>
            <Text as="p" size="4" weight="bold">
              Phase 2 · Groceries
            </Text>
            <Text as="p" size="2" className="text-[var(--color-ink)] opacity-70">
              Starts Monday, Jun 24
            </Text>
          </div>
        </div>
        <div className="mt-3.5 flex gap-1.5">
          <div className="h-1.5 flex-1 rounded-full bg-white" />
          <div className="h-1.5 flex-1 rounded-full bg-[var(--color-brand-blue)]" />
          <div className="h-1.5 flex-1 rounded-full bg-[rgba(1,38,31,0.15)]" />
        </div>
        <p className="mt-2 mb-0 text-[11px] font-medium text-[var(--color-ink)] opacity-70">
          Phase 1 complete · Phase 2 next · Phase 3 in 4 weeks
        </p>
      </div>
    </Card>
  );
}

/** Notification card pattern — unread and read states. */
export function CardNotificationDemos(): JSX.Element {
  return (
    <div className="grid gap-3.5 md:grid-cols-2">
      <Card elevation="flat" className="relative flex gap-3.5 p-4">
        <span
          className="absolute left-2 top-4 size-2 rounded-full bg-[var(--color-coral)]"
          aria-hidden
        />
        <div className="flex h-10 w-10 shrink-0 items-center justify-center text-[var(--color-ink)]">
          <Calendar size={32} strokeWidth={1.8} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <Text as="p" size="3" weight="bold" className="m-0">
            Upcoming appointment
          </Text>
          <Text as="p" size="2" className="m-0 mt-1">
            You have an appointment with an RD consultant in 30 minutes.
          </Text>
          <Button tone="primary" className="mt-2.5 h-9 px-[18px] text-sm font-medium">
            Join
          </Button>
          <div className="mt-2.5 text-xs font-bold text-[var(--color-slate-50)]">15 mins</div>
        </div>
        <button
          type="button"
          className="absolute right-3 top-2.5 border-0 bg-transparent p-0 text-lg leading-none text-[var(--color-ink-soft)]"
          aria-label="Dismiss notification"
        >
          <X size={18} aria-hidden />
        </button>
      </Card>

      <Card elevation="flat" className="relative flex gap-3.5 bg-[var(--color-stone-light)] p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center text-[var(--color-ink)] opacity-60">
          <Sun size={32} strokeWidth={1.8} aria-hidden />
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <Text as="p" size="3" weight="bold" className="m-0 opacity-70">
            New goal
          </Text>
          <Text as="p" size="2" className="m-0 mt-1 opacity-60">
            You have a new weekly goal. Follow the recommendations to achieve it.
          </Text>
          <div className="mt-2.5 text-xs font-bold text-[var(--color-slate-50)]">2 hrs ago</div>
        </div>
        <button
          type="button"
          className="absolute right-3 top-2.5 border-0 bg-transparent p-0 text-lg leading-none text-[var(--color-ink-soft)]"
          aria-label="Dismiss notification"
        >
          <X size={18} aria-hidden />
        </button>
      </Card>
    </div>
  );
}

/** Alert variant examples. */
export function AlertDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3" className="max-w-lg">
      <Alert tone="info">Informational message for the user.</Alert>
      <Alert tone="warning">Warning — review before continuing.</Alert>
      <Alert tone="danger">Something went wrong. Try again.</Alert>
      <Alert tone="success">Changes saved successfully.</Alert>
    </Flex>
  );
}

/** Aria-only Spinner (glyph only). */
export function SpinnerDemos(): JSX.Element {
  return <Spinner label="Loading content" />;
}

/** Spinner with a visible message. */
export function SpinnerMessageDemos(): JSX.Element {
  return <Spinner message="Loading content…" />;
}

/** Spinner messages derived from resource + operation. */
export function SpinnerLoadingMessageDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3">
      <Spinner message={loadingMessage({ resource: 'answer', operation: 'list' })} />
      <Spinner message={loadingMessage({ resource: 'answer', operation: 'create' })} />
      <Spinner message={loadingMessage({ resource: 'answer', operation: 'update' })} />
    </Flex>
  );
}

/** Box padding demo. */
export function BoxDemos(): JSX.Element {
  return (
    <div className="inline-block rounded-md border border-dashed border-[var(--color-field-border)]">
      <Box p="4" className="bg-[var(--color-docs-accent-soft)]">
        <Text size="2">Box with p=&quot;4&quot; (16px padding)</Text>
      </Box>
    </div>
  );
}

/** Flex row layout with gap. */
export function FlexRowDemos(): JSX.Element {
  return (
    <Flex
      direction="row"
      align="center"
      gap="4"
      className="min-h-24 rounded-lg border border-dashed border-[var(--color-field-border)] p-4"
    >
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item A</Text>
      </Box>
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item B</Text>
      </Box>
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item C</Text>
      </Box>
    </Flex>
  );
}

/** Flex column layout with gap. */
export function FlexColumnDemos(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="stretch"
      gap="3"
      className="max-w-xs rounded-lg border border-dashed border-[var(--color-field-border)] p-4"
    >
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item A</Text>
      </Box>
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item B</Text>
      </Box>
      <Box p="2" className={PREVIEW_ITEM}>
        <Text size="2">Item C</Text>
      </Box>
    </Flex>
  );
}

/** Grid columns and gap demo. */
export function GridDemos(): JSX.Element {
  return (
    <Grid columns={2} gap="3">
      {['Cell 1', 'Cell 2', 'Cell 3', 'Cell 4'].map((label) => (
        <Box key={label} p="3" className={PREVIEW_ITEM}>
          <Text size="2">{label}</Text>
        </Box>
      ))}
    </Grid>
  );
}

/** Container max-width preset demo. */
export function ContainerDemos(): JSX.Element {
  return (
    <div className="rounded-lg border border-dashed border-[var(--color-field-border)] bg-[var(--color-docs-code-bg)] p-2">
      <Container size="3">
        <Box p="4" className={PREVIEW_ITEM}>
          <Text size="2">Container size=&quot;3&quot; — max-width 880px</Text>
        </Box>
      </Container>
    </div>
  );
}

/** Section vertical padding presets. */
export function SectionDemos(): JSX.Element {
  const sizes = ['1', '2', '3', '4'] as const;

  return (
    <Flex direction="column" gap="2">
      {sizes.map((size) => (
        <div
          key={size}
          className="overflow-hidden rounded-md border border-dashed border-[var(--color-field-border)]"
        >
          <Section size={size} className="bg-[var(--color-docs-accent-soft)]">
            <Text size="2">Section size=&quot;{size}&quot;</Text>
          </Section>
        </div>
      ))}
    </Flex>
  );
}

/** Text size and color examples. */
export function TextDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="2">
      <Text as="p" size="3">
        Default body text (size 3)
      </Text>
      <Text as="p" size="2" color="muted">
        Muted supporting text (size 2)
      </Text>
      <Text as="p" size="2" color="success">
        Success message
      </Text>
      <Text as="p" size="2" color="danger">
        Error message
      </Text>
    </Flex>
  );
}

/** Heading level and size examples. */
export function HeadingDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3">
      <Heading as="h1" size="8">
        Page title (h1, size 8)
      </Heading>
      <Heading as="h2" size="6">
        Section heading (h2, size 6)
      </Heading>
      <Heading as="h3" size="5">
        Subsection heading (h3, size 5)
      </Heading>
    </Flex>
  );
}

/** Design-system type scale table (D1 → P6) at true size, matching the brand deck. */
export function HeadingScaleDemos(): JSX.Element {
  const rows: Array<{
    tag: string;
    px: string;
    specimen: JSX.Element;
    note?: string;
  }> = [
    {
      tag: 'D1',
      px: '120',
      specimen: (
        <Heading as="h1" scale="d1" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'D2',
      px: '64',
      specimen: (
        <Heading as="h1" scale="d2" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'H1',
      px: '54',
      specimen: (
        <Heading as="h1" scale="h1" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'H2',
      px: '48',
      specimen: (
        <Heading as="h2" scale="h2" className="m-0 leading-none">
          Aa
        </Heading>
      ),
      note: 'Interpolated — not in the deck',
    },
    {
      tag: 'H3',
      px: '32',
      specimen: (
        <Heading as="h2" scale="h3" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'H4',
      px: '30',
      specimen: (
        <Heading as="h3" scale="h4" className="m-0 leading-none">
          Aa
        </Heading>
      ),
      note: 'Interpolated — not in the deck',
    },
    {
      tag: 'H5',
      px: '24',
      specimen: (
        <Heading as="h3" scale="h5" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'H7',
      px: '18',
      specimen: (
        <Heading as="h4" scale="h7" className="m-0 leading-none">
          Aa
        </Heading>
      ),
    },
    {
      tag: 'P4',
      px: '16',
      specimen: (
        <Text as="p" size="3" className="m-0 leading-none">
          Aa
        </Text>
      ),
    },
    {
      tag: 'P6',
      px: '12',
      specimen: (
        <Text as="p" size="1" color="muted" className="m-0 leading-none">
          Aa
        </Text>
      ),
    },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {rows.map(({ tag, px, specimen, note }) => (
            <tr key={tag} className="border-b border-[var(--color-docs-border)] last:border-b-0">
              <td className="w-16 py-1.5 align-baseline text-[11px] font-medium uppercase tracking-wider text-[var(--color-docs-muted)]">
                {tag}
              </td>
              <td className="py-1.5 align-baseline">{specimen}</td>
              <td className="w-14 py-1.5 text-right align-baseline text-[11px] font-medium text-[var(--color-ink-soft)]">
                {px}
              </td>
              <td className="w-40 py-1.5 pl-3 align-baseline text-[11px] text-[var(--color-docs-muted)]">
                {note ?? ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-3 mb-0 text-xs text-[var(--color-docs-muted)]">
        Self-hosted Roboto ships weights 400–700 only. Design-system Light (300) is not available.
      </p>
    </div>
  );
}

/** Design-system display samples with the deck's own copy. */
export function HeadingDisplayDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="4">
      <div className="flex items-baseline gap-5">
        <Heading as="h1" scale="h1" className="m-0 flex-1">
          Hi, John!
        </Heading>
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-[var(--color-docs-muted)]">
          H1 · 54 · Bold
        </span>
      </div>
      <div className="flex items-baseline gap-5">
        <Heading as="h2" scale="h3" className="m-0 flex-1">
          Unlock food as medicine
        </Heading>
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-[var(--color-docs-muted)]">
          H3 · 32 · Bold
        </span>
      </div>
      <div className="flex items-baseline gap-5">
        <Heading as="h3" scale="h5" weight="medium" className="m-0 flex-1">
          We believe in the power of delicious, healthy food.
        </Heading>
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wider text-[var(--color-docs-muted)]">
          H5 / P1 · 24 · Medium
        </span>
      </div>
    </Flex>
  );
}

/** Modal dialog example (interactive open/close). */
export function ModalDemos(): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button tone="primary" onClick={() => setOpen(true)}>
        Open modal
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} aria-label="Example modal">
        <Heading as="h2" size="5">
          Confirm action
        </Heading>
        <Text as="p" size="2" color="muted">
          This modal closes on Escape or overlay click.
        </Text>
        <Modal.Footer>
          <Button tone="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button tone="primary" onClick={() => setOpen(false)}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

/** ConfirmDialog example for destructive actions. */
export function ConfirmDialogDemos(): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button tone="primary" onClick={() => setOpen(true)}>
        Delete item
      </Button>
      <ConfirmDialog
        open={open}
        title="Delete item"
        destructive
        confirmLabel="Delete"
        onCancel={() => setOpen(false)}
        onConfirm={() => setOpen(false)}
      >
        This action retires the item. You can restore it later by editing status.
      </ConfirmDialog>
    </>
  );
}

/** Table compound API example. */
export function TableDemos(): JSX.Element {
  const rows = [
    { name: 'Alice', status: 'Active' as const },
    { name: 'Bob', status: 'Pending' as const },
    { name: 'Carol', status: 'Active' as const },
  ];

  return (
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row) => (
          <Table.Row key={row.name}>
            <Table.Cell>{row.name}</Table.Cell>
            <Table.Cell>
              <Badge variant={row.status === 'Active' ? 'success' : 'neutral'}>{row.status}</Badge>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

/** Table with row action buttons. */
export function TableWithActionsDemos(): JSX.Element {
  const rows = [
    { name: 'Health intake', status: 'Active' as const },
    { name: 'Follow-up survey', status: 'Draft' as const },
  ];

  return (
    <Table.Root>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map((row) => (
          <Table.Row key={row.name}>
            <Table.Cell>{row.name}</Table.Cell>
            <Table.Cell>
              <Badge variant={row.status === 'Active' ? 'success' : 'neutral'}>{row.status}</Badge>
            </Table.Cell>
            <Table.Cell>
              <Flex gap="2" align="center">
                <Tooltip content="Edit survey">
                  <IconButton tone="neutral" aria-label={`Edit ${row.name}`}>
                    ✎
                  </IconButton>
                </Tooltip>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <IconButton tone="neutral" aria-label={`More actions for ${row.name}`}>
                      ⋮
                    </IconButton>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content>
                    <DropdownMenu.Item>Duplicate</DropdownMenu.Item>
                    <DropdownMenu.Item>Archive</DropdownMenu.Item>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item tone="danger">Delete</DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}

/** Table ScrollArea loading state with Spinner. */
export function TableLoadingDemos(): JSX.Element {
  return (
    <Table.ScrollArea loading loadingLabel={loadingMessage({ resource: 'row', operation: 'list' })}>
      <Table.Root>
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Alice</Table.Cell>
            <Table.Cell>
              <Badge variant="success">Active</Badge>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table.Root>
    </Table.ScrollArea>
  );
}

/** Select dropdown demo. */
export function SelectDemos(): JSX.Element {
  return (
    <div className="max-w-xs">
      <Select value="open" onChange={() => {}} aria-label="Status">
        <option value="open">Open</option>
        <option value="in-progress">In progress</option>
        <option value="closed">Closed</option>
      </Select>
    </div>
  );
}

/** Textarea demo. */
export function TextareaDemos(): JSX.Element {
  return (
    <div className="max-w-xs">
      <Textarea
        value="Member prefers morning appointments."
        onChange={() => {}}
        placeholder="Enter notes…"
        aria-label="Notes"
      />
    </div>
  );
}

/** Accordion panels demo. */
export function AccordionDemos(): JSX.Element {
  return (
    <Accordion.Root defaultExpanded="page-1" className="max-w-lg">
      <Accordion.Item value="page-1">
        <Accordion.Summary>Page 1 — Welcome</Accordion.Summary>
        <Accordion.Content>
          <Text size="2">First survey page with intro copy.</Text>
        </Accordion.Content>
      </Accordion.Item>
      <Accordion.Item value="page-2">
        <Accordion.Summary>Page 2 — Questions</Accordion.Summary>
        <Accordion.Content>
          <Text size="2">Question blocks for this page.</Text>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}

/** Autocomplete typeahead demo. */
export function AutocompleteDemos(): JSX.Element {
  const [value, setValue] = useState('text');

  return (
    <div className="max-w-xs">
      <Autocomplete
        value={value}
        onChange={setValue}
        aria-label="Question type"
        options={[
          { value: 'text', label: 'Text' },
          { value: 'choice', label: 'Single choice' },
          { value: 'multi', label: 'Multiple choice' },
        ]}
        placeholder="Select type…"
      />
    </div>
  );
}

/** MultiSelect chip dropdown demo. */
export function MultiSelectDemos(): JSX.Element {
  const [value, setValue] = useState<string[]>(['hola']);

  return (
    <div className="max-w-sm">
      <MultiSelect
        value={value}
        onChange={setValue}
        aria-label="Programs"
        placeholder="Select programs…"
        options={[
          { value: 'hola', label: 'HOLA' },
          { value: 'bcmn', label: 'BCMN' },
          { value: 'bcnc', label: 'BCNC' },
          { value: 'iuh', label: 'IUH' },
        ]}
      />
    </div>
  );
}

/** Checkbox and form control label demo. */
export function CheckboxDemos(): JSX.Element {
  const [checked, setChecked] = useState(true);

  return (
    <FormControlLabel
      control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
      label="Enable conditional logic"
    />
  );
}

/** Radio group demo. */
export function RadioGroupDemos(): JSX.Element {
  const [value, setValue] = useState('daily');

  return (
    <RadioGroup value={value} onChange={setValue} aria-label="Frequency">
      <Radio value="daily" label="Daily" />
      <Radio value="weekly" label="Weekly" />
    </RadioGroup>
  );
}

/** Icon button with tooltip demo. */
export function IconButtonDemos(): JSX.Element {
  return (
    <Tooltip content="Delete question">
      <IconButton tone="danger" aria-label="Delete question">
        ×
      </IconButton>
    </Tooltip>
  );
}

const INVOICING_STRATEGY_INFO_TOOLTIP =
  'Controls adapter invoicing when a CareBetty service completes. One-time closeout: submit an invoice on completion. Recurring monthly: bill once per billing month. Manual invoicing: no Hyphen invoicing — billing is handled outside the adapter.';

/** Standalone tooltip demo. */
export function TooltipDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="4">
      <Tooltip content="Save changes before leaving">
        <Button tone="secondary">Hover for short hint</Button>
      </Tooltip>
      <Flex align="center" gap="1">
        <Text size="2" weight="medium">
          Invoicing strategy *
        </Text>
        <Tooltip content={INVOICING_STRATEGY_INFO_TOOLTIP}>
          <IconButton
            type="button"
            tone="neutral"
            aria-label="More information about invoicing strategy"
          >
            <CircleHelp aria-hidden="true" size={16} />
          </IconButton>
        </Tooltip>
      </Flex>
    </Flex>
  );
}

/** Tabs demo matching survey builder sidebar. */
export function TabsDemos(): JSX.Element {
  const [tab, setTab] = useState('outline');

  return (
    <Tabs.Root value={tab} onValueChange={setTab}>
      <Tabs.List aria-label="Sidebar panels">
        <Tabs.Tab value="outline">Outline</Tabs.Tab>
        <Tabs.Tab value="logic">Enable logic</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="outline">
        <Text size="2">Survey page and question tree.</Text>
      </Tabs.Panel>
      <Tabs.Panel value="logic">
        <Text size="2">Conditional visibility rules.</Text>
      </Tabs.Panel>
    </Tabs.Root>
  );
}

/** Separator horizontal and vertical examples. */
export function SeparatorDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3" className="max-w-md">
      <Text size="2">Tools for building high-quality, accessible UI.</Text>
      <Separator size="3" />
      <Flex gap="3" align="center">
        <Text size="2">Themes</Text>
        <Separator orientation="vertical" size="2" />
        <Text size="2">Primitives</Text>
        <Separator orientation="vertical" size="2" />
        <Text size="2">Icons</Text>
      </Flex>
      <Separator size="4" />
      <Text size="2" color="muted">
        Use separators to divide unrelated sections without implying hierarchy.
      </Text>
    </Flex>
  );
}

/** Sidebar list demo inspired by nrx-console navigation. */
export function ListDemos(): JSX.Element {
  return (
    <nav className="max-w-xs rounded-lg border border-[var(--color-border)]">
      <List>
        <ListItemButton href="#dashboard" active>
          <ListItemIcon>
            <Home aria-hidden="true" />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItemButton>
        <ListSection label="Survey Builder" icon={<ClipboardList aria-hidden="true" />} defaultOpen>
          <ListItemButton href="#answer-bank" indent={1}>
            <ListItemText primary="Answer Bank" />
          </ListItemButton>
          <ListItemButton href="#question-bank" indent={1}>
            <ListItemText primary="Question Bank" />
          </ListItemButton>
          <ListItemButton href="#surveys" indent={1} active>
            <ListItemText primary="Surveys" />
          </ListItemButton>
          <ListItemButton href="#survey-templates" indent={1}>
            <ListItemText primary="Survey Templates" />
          </ListItemButton>
        </ListSection>
        <ListItemButton href="#organizations">
          <ListItemIcon>
            <Building2 aria-hidden="true" />
          </ListItemIcon>
          <ListItemText primary="Organizations" />
        </ListItemButton>
        <ListItemButton href="#practitioners">
          <ListItemIcon>
            <UserRound aria-hidden="true" />
          </ListItemIcon>
          <ListItemText primary="Practitioners" />
        </ListItemButton>
      </List>
    </nav>
  );
}

/** Dropdown menu demo. */
export function DropdownMenuDemos(): JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button tone="secondary">
          Options <span aria-hidden="true">▾</span>
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Item>Edit</DropdownMenu.Item>
        <DropdownMenu.Item>Duplicate</DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item>Archive</DropdownMenu.Item>
        <DropdownMenu.Separator />
        <DropdownMenu.Item tone="danger">Delete</DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

/** Chip / tag demo. */
export function ChipDemos(): JSX.Element {
  const [tags, setTags] = useState(['Draft', 'Clinical']);

  return (
    <Flex gap="2" wrap>
      {tags.map((tag) => (
        <Chip key={tag} onDelete={() => setTags((t) => t.filter((x) => x !== tag))}>
          {tag}
        </Chip>
      ))}
      <Chip variant="outlined">Read-only</Chip>
    </Flex>
  );
}

/** Chip tone × variant matrix. */
export function ChipToneDemos(): JSX.Element {
  return (
    <Flex direction="column" gap="3">
      <Flex gap="2" wrap>
        <Chip tone="neutral">Neutral</Chip>
        <Chip tone="info">Info</Chip>
        <Chip tone="warning">Warning</Chip>
        <Chip tone="danger">Danger</Chip>
        <Chip tone="success">Success</Chip>
      </Flex>
      <Flex gap="2" wrap>
        <Chip tone="neutral" variant="outlined">
          Neutral
        </Chip>
        <Chip tone="info" variant="outlined">
          Info
        </Chip>
        <Chip tone="warning" variant="outlined">
          Warning
        </Chip>
        <Chip tone="danger" variant="outlined">
          Danger
        </Chip>
        <Chip tone="success" variant="outlined">
          Success
        </Chip>
      </Flex>
    </Flex>
  );
}

/** Form label kit demo. */
export function FormAtomsDemos(): JSX.Element {
  return (
    <FormGroup className="max-w-xs">
      <FormLabel required>Question prompt</FormLabel>
      <TextInput value="" onChange={() => {}} placeholder="Enter question text…" />
      <FormHelperText>Shown to members during the survey.</FormHelperText>
    </FormGroup>
  );
}

/** Breadcrumbs demo. */
export function BreadcrumbsDemos(): JSX.Element {
  return (
    <Breadcrumbs
      items={[
        { label: 'Surveys', href: '/surveys' },
        { label: 'Health intake', href: '/surveys/1' },
        { label: 'Builder' },
      ]}
    />
  );
}

/** Snackbar feedback demo. */
export function SnackbarDemos(): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button tone="primary" onClick={() => setOpen(true)}>
        Show snackbar
      </Button>
      <Snackbar
        open={open}
        message="Survey saved successfully."
        tone="success"
        onClose={() => setOpen(false)}
        autoHideDuration={4000}
      />
    </>
  );
}
